import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../lib/AuthContext';
import { supabase } from '../../lib/supabase';
import Avatar from '../common/display/Avatar';
import { Button } from '../common/ui/Button';
import BaseCard from '../common/layout/BaseCard';
import Breadcrumb from '../common/layout/Breadcrumb';
import { ModalDialog } from '../common/ui/ModalDialog';
import { captureAppException } from '../../utils/errorCaptureUtils';
import { useNavigate } from 'react-router-dom';
import { Community } from './types';
import { captureCustomEvent } from '../../utils/posthogUtils';

interface CommunityHeaderProps {
  community: Community;
}

type ActionButtonStateKey =
  | 'banned'
  | 'pendingRequest'
  | 'invitedToJoin'
  | 'leaveCommunity'
  | 'join'
  | 'inviteOnly'
  | 'none';

interface ActionButtonStateConfig {
  text: string | (() => string);
  onClick?: () => void;
  disabled?: boolean;
  getLoadingState?: () => boolean;
}

export const CommunityHeader: React.FC<CommunityHeaderProps> = ({
  community }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [leaveErrorMessage, setLeaveErrorMessage] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null | undefined>(undefined);
  const [isAdmin, setIsAdmin] = useState(false);
  const [joinErrorMessage, setJoinErrorMessage] = useState<string | null>(null);
  const maxVisibleAvatars = 5;
  const displayedMembers = community.members.slice(0, maxVisibleAvatars);
  const remainingMembers = community.total_members - maxVisibleAvatars;

  // Get public URLs for images if they exist
  const headerImageUrl = community.header_image 
    ? supabase.storage.from('community_images').getPublicUrl(community.header_image).data.publicUrl
    : null;

  const avatarImageUrl = community.avatar_image 
    ? supabase.storage.from('community_images').getPublicUrl(community.avatar_image).data.publicUrl
    : null;

  const fetchUserRole = useCallback(async () => {
    if (!user || !community?.id) {
      setUserRole(null);
      return;
    }
    try {
      const { data: memberData, error: memberError } = await supabase
        .from('community_members')
        .select('role')
        .eq('community_id', community.id)
        .eq('user_id', user.id)
        .maybeSingle();
      if (memberError) {
        captureAppException(memberError, {
          tags: {
            context: 'community_header',
            action: 'fetch_user_role_member_check',
            community_id: community.id,
            user_id: user.id,
          },
        });
        setUserRole(null);
        return;
      }
      setUserRole(memberData ? memberData.role : null);
    } catch (error) {
      captureAppException(error, {
        tags: {
          context: 'community_header',
          action: 'fetch_user_role_general_catch',
          community_id: community.id,
          user_id: user.id,
        },
      });
      setUserRole(null);
    }
  }, [user, community?.id]);

  useEffect(() => {
    fetchUserRole();
  }, [fetchUserRole]);

  // Effect to check if the user is an admin (independent of membership role)
  useEffect(() => {
    if (!user || !community?.id) {
      setIsAdmin(false);
      return;
    }
    (async () => {
      try {
        const { data: canAdminData, error: canAdminError } = await supabase
          .rpc('community_can_admin', { check_user_id: user.id, check_community_id: community.id });
        if (canAdminError) throw canAdminError;
        setIsAdmin(!!canAdminData);
      } catch (error) {
        captureAppException(error, {
          tags: {
            context: 'community_header',
            action: 'admin_check',
            community_id: community.id,
            user_id: user.id,
          },
        });
        setIsAdmin(false);
      }
    })();
  }, [user, community?.id]);

  const handleLeaveCommunity = async () => {
    if (!user) return;
    setActionInProgress('leave_confirm');

    captureCustomEvent('community_leave_initiated', {
      eventProperties: {
        community_id: community.id,
        community_name: community.name,
        user_role: userRole
      }
    });

    try {
      // Remove the user from community_members regardless of role
      const { error: deleteError } = await supabase
        .from('community_members')
        .delete()
        .eq('community_id', community.id)
        .eq('user_id', user.id);
      if (deleteError) throw deleteError;

      captureCustomEvent('community_leave_success', {
        eventProperties: {
          community_id: community.id,
          community_name: community.name,
          user_role: userRole
        }
      });

      setShowLeaveModal(false);
      await fetchUserRole();
    } catch (error) {
      captureAppException(error, {
        tags: {
          context: 'community_header',
          action: 'leave_community_confirm',
          community_id: community.id,
          user_id: user.id,
          user_role: userRole,
        }
      });
      setLeaveErrorMessage('Unable to leave the community at this time. Please try again later.');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleJoinCommunity = async () => {
    if (!user) return;
    
    // Prevent joining invite-only communities
    if (community.access_control === 'invite_only') {
      setJoinErrorMessage('This community is invite-only. Contact an admin to join.');
      return;
    }
    
    setActionInProgress('join');
    setJoinErrorMessage(null);

    captureCustomEvent('community_join_initiated', {
      eventProperties: {
        community_id: community.id,
        community_name: community.name,
        access_control: community.access_control
      }
    });

    try {
      // Always insert as member
      const { error: insertError } = await supabase
        .from('community_members')
        .insert({
          community_id: community.id,
          user_id: user.id,
          role: 'member',
        });
      if (insertError) throw insertError;

      captureCustomEvent('community_join_success', {
        eventProperties: {
          community_id: community.id,
          community_name: community.name,
          access_control: community.access_control
        }
      });

      await fetchUserRole();
    } catch (error) {
      captureAppException(error, {
        tags: {
          context: 'community_header',
          action: 'join_community',
          community_id: community.id,
          user_id: user.id,
          user_role: userRole,
        }
      });
      setJoinErrorMessage('Unable to join the community at this time. Please try again later.');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleAcceptInvite = async () => {
    if (!user) return;
    setActionInProgress('accept_invite');
    setJoinErrorMessage(null);

    captureCustomEvent('community_invite_accepted', {
      eventProperties: {
        community_id: community.id,
        community_name: community.name,
        access_control: community.access_control
      }
    });

    try {
      // Update role from 'invited' to 'member'
      const { error: updateError } = await supabase
        .from('community_members')
        .update({ role: 'member' })
        .eq('community_id', community.id)
        .eq('user_id', user.id)
        .eq('role', 'invited');
      if (updateError) throw updateError;

      captureCustomEvent('community_invite_accept_success', {
        eventProperties: {
          community_id: community.id,
          community_name: community.name,
          access_control: community.access_control
        }
      });

      await fetchUserRole();
    } catch (error) {
      captureAppException(error, {
        tags: {
          context: 'community_header',
          action: 'accept_invite',
          community_id: community.id,
          user_id: user.id,
          user_role: userRole,
        }
      });
      setJoinErrorMessage('Unable to accept the invite at this time. Please try again later.');
    } finally {
      setActionInProgress(null);
    }
  };

  const actionButtonStateConfigs: Record<Exclude<ActionButtonStateKey, 'none'>, ActionButtonStateConfig> = {
    banned: {
      text: 'Community is unavailable',
      disabled: true,
    },
    pendingRequest: {
      text: 'Request Pending',
      disabled: true, // Or handle specific logic if needed
    },
    invitedToJoin: {
      text: 'Accept Invite',
      onClick: handleAcceptInvite,
      getLoadingState: () => actionInProgress === 'accept_invite',
    },
    leaveCommunity: {
      text: 'Leave Community',
      onClick: () => setShowLeaveModal(true),
      disabled: actionInProgress === 'leave_confirm',
      getLoadingState: () => actionInProgress === 'leave',
    },
    join: {
      text: 'Join Community',
      onClick: handleJoinCommunity,
      getLoadingState: () => actionInProgress === 'join',
    },
    inviteOnly: {
      text: 'Invite Only',
      disabled: true,
    },
  };

  const determineActionButtonState = (): ActionButtonStateKey => {
    if (userRole === 'banned') return 'banned';
    if (userRole === 'pending') return 'pendingRequest';
    if (userRole === 'invited') return 'invitedToJoin';
    if (userRole === 'member') return 'leaveCommunity';
    if (community.access_control === 'invite_only') return 'inviteOnly';
    return 'join';
  };

  const currentButtonStateKey = determineActionButtonState();
  const activeActionConfig = currentButtonStateKey !== 'none' ? actionButtonStateConfigs[currentButtonStateKey as Exclude<ActionButtonStateKey, 'none'>] : null;

  return (
    <>
      <BaseCard overflow='visible'>
        <div className="relative min-h-[12rem] rounded-2xl overflow-hidden">
          {/* Background image */}
          {headerImageUrl && (
            <img
              src={headerImageUrl}
              alt={community.name}
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}

          {/* Dark overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-black/70" />

          {/* Left side darkening gradient */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent" />

          {/* Content */}
          <div className="relative p-6 pb-8">
            {/* Top row with breadcrumb and avatars */}
            <div className="flex justify-between items-start mb-4">
              {/* Breadcrumb */}
              <Breadcrumb
                items={[{ label: 'Communities' }, { label: community.name }]}
                onNavigate={(_, index) => (index === 0) && navigate('/communities')}
                theme="dark"
              />

              {/* Member avatars */}
              <div className="flex -space-x-2">
                {displayedMembers.map((member) => (
                  <Avatar
                    key={member.user_id}
                    firstName={member.first_name ?? ''}
                    lastName={member.last_name ?? ''}
                    size="small"
                    className=""
                  />
                ))}
                {remainingMembers > 0 && (
                  <div className="relative w-10 h-10 flex items-center justify-center bg-gray-900 text-white text-xs font-bold rounded-full">
                    +{remainingMembers}
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  {avatarImageUrl ? (
                    <img
                      src={avatarImageUrl}
                      alt={`${community.name} logo`}
                      className="w-16 h-16 rounded-full object-cover shadow-lg"
                    />
                  ) : (
                    <Avatar
                      firstName={community.name}
                      size="medium"
                      className="flex-shrink-0"
                    />
                  )}
                  <h1 className="text-2xl font-bold text-white">
                    {community.name}
                  </h1>
                </div>
              </div>
            </div>

            {/* Actions row with buttons and verified tag */}
            <div className="flex justify-between items-center mt-6">
              <div>
                {activeActionConfig && (
                  <p className="rounded-lg bg-white/10 px-3 py-2 text-sm font-medium text-white">
                    Membership changes are disabled while Holito Communities is being decommissioned.
                  </p>
                )}
                {joinErrorMessage && <p className="text-sm text-red-400 mt-2">{joinErrorMessage}</p>}
              </div>

              {isAdmin && (
                <Button
                  variant="filled"
                  color="secondary"
                  size="small"
                  onClick={() => navigate(`/communities/${community.id}/manage`)}
                  className="ml-2"
                >
                  Manage Community
                </Button>
              )}
            </div>
          </div>
        </div>
      </BaseCard>
      <ModalDialog
        isOpen={showLeaveModal}
        onClose={() => {
          setShowLeaveModal(false);
          setLeaveErrorMessage(null);
          setActionInProgress(null);
        }}
        title="Leave Community"
        description={leaveErrorMessage || 'Are you sure you want to leave this community?'}
        variant={leaveErrorMessage ? 'singleButton' : 'twoButton'}
        primaryButtonText={leaveErrorMessage ? 'OK' : 'Leave Community'}
        secondaryButtonText="Cancel"
        onPrimaryButtonClick={leaveErrorMessage ? () => {
          setShowLeaveModal(false);
          setLeaveErrorMessage(null);
          setActionInProgress(null);
        } : handleLeaveCommunity}
        primaryButtonLoading={actionInProgress === 'leave_confirm'}
        primaryButtonColor={leaveErrorMessage ? 'primary' : 'danger'}
        icon={leaveErrorMessage ? 'error' : 'warning'}
      />
    </>
  );
};